import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import portalApi, { apiError } from './portalApi';
import { T, sans } from './portalTheme';
import {
  PortalAppShell, PageContainer, Card, CardHeader, Avatar, StatusPill, DueBadge, ProgressBar, Button, EmptyState, Alert,
  Icon, IC, Spinner, Pill, VerticalStepper, Field, TextInput, inputStyle, fmtDate, ResponseStatus,
} from './PortalWidgets';

interface EvidenceDoc {
  id: string;
  file_name: string;
  content_type: string | null;
  size_bytes: number | null;
  document_type: string | null;
  description?: string | null;
  created_at: string;
}

interface ResponseDetail {
  id: string;
  status: ResponseStatus;
  recipient_name: string | null;
  recipient_email: string;
  message: string | null;
  due_date: string | null;
  sent_at: string;
  opened_at: string | null;
  answered_at: string | null;
  reviewed_at: string | null;
  answered_count: number;
  questionnaire_name: string;
  total_items: number;
  vendor_name: string;
  sender_org_name: string;
  is_primary_recipient: boolean;
}

interface Collaborator {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  accepted: boolean;
  accepted_at: string | null;
  answered_count: number;
  invited_at: string;
}

type Tab = 'overview' | 'team' | 'documents' | 'activity';

export const DOCUMENT_TYPES = ['SOC 2 Report', 'ISO 27001 Certificate', 'Penetration Test Report', 'Security Policy',
  'Privacy Policy', 'Audit Report', 'Insurance Certificate', 'Other'];
export const ACCEPTED_FILES = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt';
export const fmtSize = (bytes: number | null) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
export function FileBadge({ name }: { name: string }) {
  const ext = (name.split('.').pop() || '').toLowerCase();
  const m = ext === 'pdf' ? ['PDF', '#DC2626'] : ['doc', 'docx'].includes(ext) ? ['DOC', '#2563EB'] : ['xls', 'xlsx'].includes(ext) ? ['XLS', '#059669']
    : ['png', 'jpg', 'jpeg'].includes(ext) ? ['IMG', '#7C3AED'] : [ext.slice(0, 3).toUpperCase() || 'FILE', '#64748B'];
  return (
    <span style={{ width: 36, height: 36, borderRadius: 8, background: `${m[1]}14`, color: m[1], border: `1px solid ${m[1]}30`, display: 'inline-flex',
      alignItems: 'center', justifyContent: 'center', fontSize: 9.5, fontWeight: 800, letterSpacing: '.03em', flexShrink: 0, fontFamily: sans }}>{m[0]}</span>
  );
}

const STAGE_OF: Record<string, number> = { sent: 0, opened: 1, answered: 2, reviewed: 3 };
const collabName = (c: Collaborator) => [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email;

// /questionnaire/portal/response/:responseId -- the overview of one questionnaire, in front of the
// answering page itself (AnswerQuestionnaire.tsx, reached via Start / Continue).
export default function PortalResponseDetail() {
  const { responseId } = useParams<{ responseId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('overview');
  const [data, setData] = useState<ResponseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [docs, setDocs] = useState<EvidenceDoc[]>([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [collabLoading, setCollabLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);

  useEffect(() => {
    if (!responseId) return;
    portalApi.get('/v1/questionnaire-responses/portal/response/detail', { params: { response_id: responseId } })
      .then(({ data }) => setData(data))
      .catch(err => setLoadError(apiError(err, 'Could not load this questionnaire.')))
      .finally(() => setLoading(false));
    loadDocs();
    loadCollaborators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseId]);

  const loadDocs = () => {
    if (!responseId) return;
    portalApi.get('/v1/questionnaire-responses/portal/evidence', { params: { response_id: responseId } })
      .then(({ data }) => setDocs(data.documents || []))
      .catch(() => setDocs([]))
      .finally(() => setDocsLoading(false));
  };

  const loadCollaborators = () => {
    if (!responseId) return;
    portalApi.get('/v1/questionnaire-responses/portal/collaborators', { params: { response_id: responseId } })
      .then(({ data }) => setCollaborators(data.collaborators || []))
      .catch(() => setCollaborators([]))
      .finally(() => setCollabLoading(false));
  };

  if (loading) {
    return <PortalAppShell><PageContainer><div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><Spinner size={30} /></div></PageContainer></PortalAppShell>;
  }
  if (loadError || !data) {
    return (
      <PortalAppShell>
        <PageContainer>
          <Card>
            <EmptyState icon={IC.alert} title="This questionnaire isn't available"
              action={<Button variant="secondary" icon={IC.back} onClick={() => navigate('/questionnaire/portal/dashboard')}>Back to questionnaires</Button>}>
              {loadError || 'It may have been removed, or you may no longer have access to it.'}
            </EmptyState>
          </Card>
        </PageContainer>
      </PortalAppShell>
    );
  }

  const isOpen = data.status === 'sent' || data.status === 'opened';
  const pct = data.total_items ? Math.round((data.answered_count / data.total_items) * 100) : 0;
  const recipientLabel = data.recipient_name || data.recipient_email;
  const cta = data.status === 'sent' ? 'Start questionnaire' : data.status === 'opened' ? 'Continue answering' : data.status === 'answered' ? 'View submission' : null;

  const tabs: { key: Tab; label: string; count?: number }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'team', label: 'Team', count: collaborators.length + 1 },
    { key: 'documents', label: 'Documents', count: docs.length },
    { key: 'activity', label: 'Activity' },
  ];

  return (
    <PortalAppShell>
      <PageContainer>
        <Link to="/questionnaire/portal/dashboard" className="pw-link" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: T.slate, textDecoration: 'none', marginBottom: 16 }}>
          <Icon d={IC.back} size={14} /> All questionnaires
        </Link>

        {/* ── Header ── */}
        <Card padding={0} style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start', padding: '24px 24px 20px', flexWrap: 'wrap' }}>
            <Avatar name={data.sender_org_name} size={52} square />
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h1 style={{ fontSize: 23, fontWeight: 800, color: T.ink, margin: 0, letterSpacing: -0.3 }}>{data.questionnaire_name}</h1>
                <StatusPill status={data.status} />
                {!data.is_primary_recipient && <Pill color={T.violet} bg={T.violetBg} dot={false}>Collaborating</Pill>}
              </div>
              <div style={{ fontSize: 14, color: T.muted, marginTop: 6 }}>
                Requested by <strong style={{ color: T.ink2 }}>{data.sender_org_name}</strong> for <strong style={{ color: T.ink2 }}>{data.vendor_name}</strong>
              </div>
            </div>
            {cta && (
              <Button size="lg" onClick={() => window.open(`/questionnaire/portal/answer/${data.id}`, '_blank', 'noopener,noreferrer')} variant={isOpen ? 'primary' : 'secondary'}>
                {cta} <Icon d={IC.chevRight} size={15} />
              </Button>
            )}
          </div>
          <div className="pw-grid-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', borderTop: `1px solid ${T.bdr}`, background: T.surface2, borderRadius: '0 0 12px 12px' }}>
            <div style={{ padding: '14px 24px', borderRight: `1px solid ${T.bdr}` }}>
              <div style={statLabel}>Progress</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 4 }}>
                <span style={{ fontSize: 20, fontWeight: 800, color: T.ink, fontVariantNumeric: 'tabular-nums' }}>{pct}%</span>
                <span style={{ fontSize: 12.5, color: T.muted }}>{data.answered_count} of {data.total_items}</span>
              </div>
              <div style={{ marginTop: 6 }}><ProgressBar value={data.answered_count} total={data.total_items} height={5} /></div>
            </div>
            <div style={{ padding: '14px 24px', borderRight: `1px solid ${T.bdr}` }}>
              <div style={statLabel}>Due date</div>
              <div style={{ marginTop: 4 }}><DueBadge due={data.due_date} status={data.status} /></div>
            </div>
            <div style={{ padding: '14px 24px', borderRight: `1px solid ${T.bdr}` }}>
              <div style={statLabel}>Received</div>
              <div style={{ fontSize: 13, color: T.ink2, fontWeight: 500, marginTop: 4 }}>{fmtDate(data.sent_at)}</div>
            </div>
            <div style={{ padding: '14px 24px' }}>
              <div style={statLabel}>Your role</div>
              <div style={{ fontSize: 13, color: T.ink2, fontWeight: 500, marginTop: 4 }}>
                {data.is_primary_recipient ? 'Recipient — you submit' : `Collaborator — ${recipientLabel} submits`}
              </div>
            </div>
          </div>
        </Card>

        {/* ── Tabs ── */}
        <div role="tablist" style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${T.bdr}`, marginBottom: 20, overflowX: 'auto', overflowY: 'hidden' }}>
          {tabs.map(t => {
            const on = tab === t.key;
            return (
              <button key={t.key} role="tab" aria-selected={on} onClick={() => setTab(t.key)} style={{
                display: 'inline-flex', alignItems: 'center', gap: 7, padding: '10px 14px', marginBottom: -1, background: 'none', border: 'none',
                borderBottom: `2px solid ${on ? T.primary : 'transparent'}`, cursor: 'pointer', fontFamily: sans, fontSize: 13.5, fontWeight: 600,
                color: on ? T.primary : T.slate, whiteSpace: 'nowrap',
              }}>
                {t.label}
                {t.count !== undefined && (
                  <span style={{ fontSize: 11.5, fontWeight: 700, background: on ? T.blueBg : T.hover, color: on ? T.primary : T.slate, borderRadius: 99, padding: '1px 7px' }}>{t.count}</span>
                )}
              </button>
            );
          })}
        </div>

        {tab === 'overview' && (
          <OverviewTab data={data} docs={docs} docsLoading={docsLoading} collaborators={collaborators} collabLoading={collabLoading}
            onGo={(t) => setTab(t)} onAnswer={() => window.open(`/questionnaire/portal/answer/${data.id}`, '_blank', 'noopener,noreferrer')} onInvite={() => { setTab('team'); setInviteOpen(true); }} />
        )}
        {tab === 'team' && (
          <TeamTab data={data} collaborators={collaborators} loading={collabLoading} onInvite={() => setInviteOpen(true)}
            onRemoved={(id) => setCollaborators(prev => prev.filter(c => c.id !== id))} onError={loadCollaborators} />
        )}
        {tab === 'documents' && (
          <DocumentsTab responseId={data.id} docs={docs} loading={docsLoading} canEdit={isOpen} senderOrg={data.sender_org_name} onChanged={loadDocs} />
        )}
        {tab === 'activity' && <ActivityTab data={data} collaborators={collaborators} />}
      </PageContainer>

      {inviteOpen && (
        <InviteCollaboratorsModal
          responseId={data.id}
          existing={collaborators}
          onClose={() => setInviteOpen(false)}
          onInvited={() => { setInviteOpen(false); loadCollaborators(); queryClient.invalidateQueries({ queryKey: ['portal', 'me'] }); }}
        />
      )}
    </PortalAppShell>
  );
}

const statLabel: React.CSSProperties = { fontSize: 11.5, fontWeight: 700, color: T.faint, textTransform: 'uppercase', letterSpacing: '.05em' };

// ─── Overview ─────────────────────────────────────────────────────────────────────────────────

function OverviewTab({ data, docs, docsLoading, collaborators, collabLoading, onGo, onAnswer, onInvite }: {
  data: ResponseDetail; docs: EvidenceDoc[]; docsLoading: boolean; collaborators: Collaborator[]; collabLoading: boolean;
  onGo: (t: Tab) => void; onAnswer: () => void; onInvite: () => void;
}) {
  const isOpen = data.status === 'sent' || data.status === 'opened';
  const allAnswered = data.total_items > 0 && data.answered_count >= data.total_items;
  const submitted = data.status === 'answered' || data.status === 'reviewed';
  const remaining = Math.max(data.total_items - data.answered_count, 0);
  const recipientLabel = data.recipient_name || data.recipient_email;

  const steps = [
    { done: allAnswered || submitted, title: 'Answer every question',
      body: submitted ? 'All questions were answered.' : allAnswered ? 'All questions answered.' : `${remaining} question${remaining === 1 ? '' : 's'} still to answer.`,
      action: isOpen && !allAnswered ? <Button size="sm" onClick={onAnswer}>{data.status === 'sent' ? 'Start' : 'Continue'}</Button> : null },
    { done: docs.length > 0, optional: true, title: 'Attach supporting evidence',
      body: docsLoading ? 'Checking your documents…' : docs.length ? `${docs.length} document${docs.length === 1 ? '' : 's'} in your library for ${data.sender_org_name}.` : 'Upload a SOC 2 report, policy or certificate and attach it to the questions it supports.',
      action: isOpen ? <Button size="sm" variant="secondary" onClick={() => onGo('documents')}>{docs.length ? 'Manage' : 'Upload'}</Button> : null },
    ...(data.is_primary_recipient ? [{ done: collaborators.length > 0, optional: true, title: 'Invite colleagues to help',
      body: collabLoading ? 'Checking your team…' : collaborators.length ? `${collaborators.length} colleague${collaborators.length === 1 ? '' : 's'} helping.` : 'Bring in the people who know the answers — IT, legal, HR.',
      action: isOpen ? <Button size="sm" variant="secondary" onClick={onInvite}>Invite</Button> : null }] : []),
    { done: submitted, title: data.is_primary_recipient ? 'Submit to ' + data.sender_org_name : `${recipientLabel} submits`,
      body: submitted ? `Submitted on ${fmtDate(data.answered_at)}.` : data.is_primary_recipient
        ? 'Review your answers, then press Submit at the bottom of the questionnaire.'
        : `Only ${recipientLabel} can submit. Your answers are saved as you go.`,
      action: null },
  ];

  return (
    <div className="pw-grid-2" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)', gap: 20, alignItems: 'start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {data.status === 'reviewed' && (
          <Alert tone="success">{data.sender_org_name} reviewed your submission on {fmtDate(data.reviewed_at)}. No further action is needed.</Alert>
        )}
        {data.status === 'answered' && (
          <Alert tone="info">Submitted on {fmtDate(data.answered_at)}. {data.sender_org_name} will review your answers and may follow up with questions.</Alert>
        )}

        {data.message && (
          <Card>
            <div style={{ display: 'flex', gap: 12 }}>
              <Avatar name={data.sender_org_name} size={34} square />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>Message from {data.sender_org_name}</div>
                <div style={{ fontSize: 12, color: T.faint, marginTop: 1 }}>{fmtDate(data.sent_at)}</div>
                <div style={{ fontSize: 14, color: T.ink2, lineHeight: 1.65, marginTop: 10, whiteSpace: 'pre-wrap' }}>{data.message}</div>
              </div>
            </div>
          </Card>
        )}

        <Card>
          <CardHeader title={submitted ? 'Steps' : 'What to do next'} subtitle={submitted ? undefined : 'Your progress is saved automatically — come back any time.'} />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {steps.map((s, i) => (
              <div key={s.title} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '14px 0', borderTop: i ? `1px solid ${T.bdr}` : 'none' }}>
                <span style={{ width: 26, height: 26, borderRadius: '50%', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  background: s.done ? T.green : T.surface, border: `2px solid ${s.done ? T.green : T.bdr}`, color: '#fff', fontSize: 12, fontWeight: 700 }}>
                  {s.done ? <Icon d={IC.check} size={13} sw={3} /> : <span style={{ color: T.faint }}>{i + 1}</span>}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: s.done ? T.slate : T.ink, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {s.title}
                    {'optional' in s && s.optional && !s.done && <span style={{ fontSize: 11, fontWeight: 600, color: T.faint }}>Optional</span>}
                  </div>
                  <div style={{ fontSize: 13, color: T.muted, marginTop: 3, lineHeight: 1.5 }}>{s.body}</div>
                </div>
                {s.action}
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <Card>
          <CardHeader title="Status" />
          <VerticalStepper stage={STAGE_OF[data.status]} dates={[data.sent_at, data.opened_at, data.answered_at, data.reviewed_at]} />
        </Card>
        <Card>
          <CardHeader title="Details" />
          {[
            ['Questionnaire', data.questionnaire_name],
            ['Requested by', data.sender_org_name],
            ['For', data.vendor_name],
            ['Sent to', recipientLabel],
            ['Questions', String(data.total_items)],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '9px 0', borderTop: `1px solid ${T.hover}`, fontSize: 13 }}>
              <span style={{ color: T.muted }}>{k}</span>
              <span style={{ color: T.ink2, fontWeight: 500, textAlign: 'right', wordBreak: 'break-word' }}>{v}</span>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

// ─── Team ─────────────────────────────────────────────────────────────────────────────────────

function TeamTab({ data, collaborators, loading, onInvite, onRemoved, onError }: {
  data: ResponseDetail; collaborators: Collaborator[]; loading: boolean; onInvite: () => void; onRemoved: (id: string) => void; onError: () => void;
}) {
  const [confirm, setConfirm] = useState<Collaborator | null>(null);
  const [removing, setRemoving] = useState(false);
  const isOpen = data.status === 'sent' || data.status === 'opened';

  const remove = async () => {
    if (!confirm) return;
    setRemoving(true);
    try {
      await portalApi.delete('/v1/questionnaire-responses/portal/collaborators', { params: { collaborator_id: confirm.id, response_id: data.id } });
      onRemoved(confirm.id);
      setConfirm(null);
    } catch {
      onError();
      setConfirm(null);
    } finally {
      setRemoving(false);
    }
  };

  const th: React.CSSProperties = { textAlign: 'left', padding: '10px 16px', fontSize: 11.5, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: T.faint, background: T.surface2, borderBottom: `1px solid ${T.bdr}` };
  const td: React.CSSProperties = { padding: '14px 16px', borderBottom: `1px solid ${T.bdr}`, fontSize: 13, verticalAlign: 'middle' };

  return (
    <Card padding={0} style={{ overflow: 'hidden' }}>
      <div style={{ padding: '20px 20px 4px' }}>
        <CardHeader
          title="Team"
          subtitle={data.is_primary_recipient
            ? 'Invite colleagues to help answer. They can answer questions, attach evidence and read findings — only you can submit.'
            : `People helping ${data.recipient_name || data.recipient_email} with this questionnaire. Only they can submit it.`}
          action={data.is_primary_recipient && isOpen ? <Button icon={IC.plus} onClick={onInvite}>Invite collaborators</Button> : undefined}
        />
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
          <thead>
            <tr><th style={th}>Person</th><th style={th}>Role</th><th style={th}>Questions answered</th><th style={th}>Invited</th><th style={{ ...th, width: 90 }} aria-label="Actions" /></tr>
          </thead>
          <tbody>
            <tr>
              <td style={td}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Avatar name={data.recipient_name || data.recipient_email} size={34} />
                  <div>
                    <div style={{ fontWeight: 600, color: T.ink }}>{data.recipient_name || data.recipient_email}</div>
                    <div style={{ fontSize: 12, color: T.muted }}>{data.recipient_email}</div>
                  </div>
                </div>
              </td>
              <td style={td}><Pill color={T.green} bg={T.greenBg}>Recipient</Pill></td>
              <td style={{ ...td, color: T.muted }}>Can submit</td>
              <td style={{ ...td, color: T.muted }}>{fmtDate(data.sent_at)}</td>
              <td style={td} />
            </tr>
            {loading ? (
              <tr><td colSpan={5} style={{ ...td, textAlign: 'center' }}><Spinner /></td></tr>
            ) : collaborators.map(c => (
              <tr key={c.id}>
                <td style={td}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar name={collabName(c)} size={34} />
                    <div>
                      <div style={{ fontWeight: 600, color: T.ink }}>{collabName(c)}</div>
                      <div style={{ fontSize: 12, color: T.muted }}>{c.email}</div>
                    </div>
                  </div>
                </td>
                <td style={td}>{c.accepted ? <Pill color={T.primary} bg={T.blueBg}>Collaborator</Pill> : <Pill color={T.amber} bg={T.amberBg}>Invited</Pill>}</td>
                <td style={td}>
                  <span style={{ fontWeight: 700, color: T.ink, fontVariantNumeric: 'tabular-nums' }}>{c.answered_count}</span>
                  <span style={{ color: T.muted }}> of {data.total_items}</span>
                </td>
                <td style={{ ...td, color: T.muted }}>{fmtDate(c.invited_at)}</td>
                <td style={{ ...td, textAlign: 'right' }}>
                  {data.is_primary_recipient && (
                    <Button size="sm" variant="ghost" onClick={() => setConfirm(c)} aria-label={`Remove ${collabName(c)}`} style={{ color: T.red }}>Remove</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!loading && collaborators.length === 0 && (
        <div style={{ padding: '28px 20px', textAlign: 'center', fontSize: 13.5, color: T.muted }}>
          {data.is_primary_recipient
            ? <>No collaborators yet. {isOpen && <button onClick={onInvite} className="pw-link" style={{ background: 'none', border: 'none', color: T.primary, fontWeight: 600, cursor: 'pointer', fontFamily: sans, fontSize: 13.5, padding: 0 }}>Invite a colleague</button>}</>
            : 'No other collaborators.'}
        </div>
      )}

      {confirm && (
        <Modal title="Remove collaborator?" onClose={() => !removing && setConfirm(null)} footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)} disabled={removing}>Cancel</Button>
            <Button variant="danger" onClick={remove} loading={removing} style={{ background: T.red, color: '#fff', borderColor: T.red }}>Remove</Button>
          </>
        }>
          <div style={{ fontSize: 14, color: T.slate, lineHeight: 1.6 }}>
            <strong style={{ color: T.ink }}>{collabName(confirm)}</strong> will lose access to this questionnaire immediately.
            Answers they've already given stay in place.
          </div>
        </Modal>
      )}
    </Card>
  );
}

// ─── Documents ────────────────────────────────────────────────────────────────────────────────

export function DocumentsTab({ responseId, docs, loading, canEdit, senderOrg, onChanged }: {
  responseId: string; docs: EvidenceDoc[]; loading: boolean; canEdit: boolean; senderOrg: string; onChanged: () => void;
}) {
  return (
    <Card>
      <CardHeader title="Documents" subtitle={`Your evidence library for ${senderOrg}. Anything here can be attached to any question in this and future questionnaires from them.`} />
      {canEdit && <UploadZone responseId={responseId} onUploaded={onChanged} />}
      <div style={{ marginTop: canEdit ? 20 : 0 }}>
        <DocumentList responseId={responseId} docs={docs} loading={loading} canDelete={canEdit} onChanged={onChanged} />
      </div>
    </Card>
  );
}

export function UploadZone({ responseId, onUploaded }: { responseId: string; onUploaded: () => void }) {
  const [documentType, setDocumentType] = useState(DOCUMENT_TYPES[0]);
  const [description, setDescription] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = async (files: FileList | null) => {
    if (!files || !files.length) return;
    setUploading(true);
    setError('');
    setDone('');
    const ok: string[] = [];
    for (const file of Array.from(files)) {
      try {
        const form = new FormData();
        form.append('response_id', responseId);
        form.append('document_type', documentType);
        if (description.trim()) form.append('description', description.trim());
        form.append('file', file);
        await portalApi.post('/v1/questionnaire-responses/portal/evidence', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        ok.push(file.name);
      } catch (err) {
        setError(`${file.name}: ${apiError(err, 'could not be uploaded.')}`);
      }
    }
    setUploading(false);
    if (ok.length) { setDone(`Uploaded ${ok.length === 1 ? ok[0] : `${ok.length} files`}.`); setDescription(''); onUploaded(); }
  };

  return (
    <div>
      <div className="pw-grid-2" style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 12, marginBottom: 12 }}>
        <Field label="Document type">
          <select value={documentType} onChange={e => setDocumentType(e.target.value)} className="pw-input" style={{ ...inputStyle, cursor: 'pointer' }}>
            {DOCUMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Note (optional)">
          <TextInput value={description} onChange={e => setDescription(e.target.value)} placeholder='E.g. "FY2026 Type II, covers all regions"' />
        </Field>
      </div>
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files); }}
        onClick={() => !uploading && inputRef.current?.click()}
        role="button" tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click(); } }}
        style={{ border: `2px dashed ${dragOver ? T.primary : T.bdrStrong}`, background: dragOver ? T.blueBg : T.surface2, borderRadius: 12,
          padding: '26px 16px', textAlign: 'center', cursor: uploading ? 'wait' : 'pointer', transition: 'all .12s' }}>
        {uploading ? <Spinner size={24} /> : (
          <span style={{ width: 40, height: 40, borderRadius: 11, background: T.blueBg, color: T.primary, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon d={IC.upload} size={19} />
          </span>
        )}
        <div style={{ fontSize: 14, fontWeight: 600, color: T.ink, marginTop: 10 }}>
          {uploading ? 'Uploading…' : <>Drop files here or <span style={{ color: T.primary }}>browse</span></>}
        </div>
        <div style={{ fontSize: 12.5, color: T.muted, marginTop: 4 }}>PDF, Word, Excel, images or text · up to 15 MB each</div>
        <input ref={inputRef} type="file" multiple accept={ACCEPTED_FILES} style={{ display: 'none' }} onChange={e => { upload(e.target.files); e.target.value = ''; }} />
      </div>
      {error && <div style={{ marginTop: 12 }}><Alert tone="error">{error}</Alert></div>}
      {done && !error && <div style={{ marginTop: 12 }}><Alert tone="success">{done}</Alert></div>}
    </div>
  );
}

export function DocumentList({ responseId, docs, loading, canDelete, onChanged }: {
  responseId: string; docs: EvidenceDoc[]; loading: boolean; canDelete: boolean; onChanged: () => void;
}) {
  const [confirm, setConfirm] = useState<EvidenceDoc | null>(null);
  const [busy, setBusy] = useState(false);

  const download = async (doc: EvidenceDoc) => {
    const res = await portalApi.get('/v1/questionnaire-responses/portal/evidence/download', { params: { document_id: doc.id, response_id: responseId }, responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = doc.file_name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const remove = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      await portalApi.delete('/v1/questionnaire-responses/portal/evidence', { params: { document_id: confirm.id, response_id: responseId } });
    } finally {
      setBusy(false);
      setConfirm(null);
      onChanged();
    }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><Spinner /></div>;
  if (!docs.length) {
    return <div style={{ textAlign: 'center', padding: '22px 12px', fontSize: 13.5, color: T.muted }}>No documents yet.</div>;
  }
  return (
    <div style={{ border: `1px solid ${T.bdr}`, borderRadius: 10, overflow: 'hidden' }}>
      {docs.map((doc, i) => (
        <div key={doc.id} className="pw-row" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderTop: i ? `1px solid ${T.bdr}` : 'none' }}>
          <FileBadge name={doc.file_name} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.file_name}</div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {[doc.document_type, fmtSize(doc.size_bytes), fmtDate(doc.created_at), doc.description].filter(Boolean).join(' · ')}
            </div>
          </div>
          <Button size="sm" variant="ghost" icon={IC.download} onClick={() => download(doc)} aria-label={`Download ${doc.file_name}`}>
            <span className="pw-hide-sm">Download</span>
          </Button>
          {canDelete && (
            <Button size="sm" variant="ghost" onClick={() => setConfirm(doc)} aria-label={`Remove ${doc.file_name}`} style={{ color: T.red }}>
              <Icon d={IC.trash} size={14} />
            </Button>
          )}
        </div>
      ))}
      {confirm && (
        <Modal title="Remove document?" onClose={() => !busy && setConfirm(null)} footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
            <Button onClick={remove} loading={busy} style={{ background: T.red, borderColor: T.red }}>Remove</Button>
          </>
        }>
          <div style={{ fontSize: 14, color: T.slate, lineHeight: 1.6 }}>
            <strong style={{ color: T.ink }}>{confirm.file_name}</strong> will be removed from your library and from every question it's attached to.
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Activity ─────────────────────────────────────────────────────────────────────────────────

function ActivityTab({ data, collaborators }: { data: ResponseDetail; collaborators: Collaborator[] }) {
  const recipient = data.recipient_name || data.recipient_email;
  const events = useMemo(() => {
    const list: { at: string; icon: string; color: string; text: React.ReactNode }[] = [];
    list.push({ at: data.sent_at, icon: IC.send, color: T.slate, text: <><strong>{data.sender_org_name}</strong> sent the questionnaire to {recipient}</> });
    if (data.opened_at) list.push({ at: data.opened_at, icon: IC.inbox, color: T.primary, text: <>The questionnaire was opened</> });
    collaborators.forEach(c => {
      list.push({ at: c.invited_at, icon: IC.users, color: T.violet, text: <>{recipient} invited <strong>{collabName(c)}</strong> to collaborate</> });
      if (c.accepted_at) list.push({ at: c.accepted_at, icon: IC.user, color: T.primary, text: <><strong>{collabName(c)}</strong> joined</> });
    });
    if (data.answered_at) list.push({ at: data.answered_at, icon: IC.checkCircle, color: T.violet, text: <>Submitted to <strong>{data.sender_org_name}</strong></> });
    if (data.reviewed_at) list.push({ at: data.reviewed_at, icon: IC.shield, color: T.green, text: <><strong>{data.sender_org_name}</strong> completed their review</> });
    return list.sort((a, b) => b.at.localeCompare(a.at));
  }, [data, collaborators, recipient]);

  return (
    <Card>
      <CardHeader title="Activity" subtitle="Everything that has happened on this questionnaire, newest first." />
      <div>
        {events.map((e, i) => (
          <div key={i} style={{ display: 'flex', gap: 14 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <span style={{ width: 32, height: 32, borderRadius: '50%', background: `${e.color}14`, color: e.color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon d={e.icon} size={15} />
              </span>
              {i < events.length - 1 && <div style={{ width: 2, flex: 1, minHeight: 18, background: T.bdr }} />}
            </div>
            <div style={{ paddingBottom: i < events.length - 1 ? 18 : 0, paddingTop: 5 }}>
              <div style={{ fontSize: 13.5, color: T.ink2, lineHeight: 1.5 }}>{e.text}</div>
              <div style={{ fontSize: 12, color: T.faint, marginTop: 2 }}>
                {new Date(e.at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─── Modal + invite ───────────────────────────────────────────────────────────────────────────

export function Modal({ title, children, footer, onClose, width = 520 }: { title: string; children: React.ReactNode; footer?: React.ReactNode; onClose: () => void; width?: number }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 16 }}>
      <div role="dialog" aria-modal="true" aria-label={title} style={{ width: '100%', maxWidth: width, maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        background: T.surface, borderRadius: 14, fontFamily: sans, boxShadow: '0 24px 60px rgba(15,23,42,.25)', animation: 'pwFadeIn .15s ease-out' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', borderBottom: `1px solid ${T.bdr}` }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: T.ink }}>{title}</div>
          <button onClick={onClose} aria-label="Close" className="pw-nav" style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.muted, padding: 6, borderRadius: 7, display: 'inline-flex' }}>
            <Icon d={IC.close} size={17} />
          </button>
        </div>
        <div style={{ padding: '20px 22px', overflowY: 'auto' }}>{children}</div>
        {footer && <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 22px', borderTop: `1px solid ${T.bdr}`, background: T.surface2, borderRadius: '0 0 14px 14px' }}>{footer}</div>}
      </div>
    </div>
  );
}

interface CollaboratorEntry { email: string; first_name: string; last_name: string }
const blankEntry = (): CollaboratorEntry => ({ email: '', first_name: '', last_name: '' });

// Mirrors UpGuard's "Invite collaborators" dialog (reviewed live in UpGuard's Trust Exchange
// before building): several new collaborators at once (First name / Last name / Email address,
// "+ Add another collaborator"), a shared message sent to all of them, Cancel / Send invites.
function InviteCollaboratorsModal({ responseId, existing, onClose, onInvited }: {
  responseId: string; existing: Collaborator[]; onClose: () => void; onInvited: () => void;
}) {
  const [entries, setEntries] = useState<CollaboratorEntry[]>([blankEntry()]);
  const [message, setMessage] = useState('As discussed, please help us complete this security questionnaire.');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const update = (i: number, patch: Partial<CollaboratorEntry>) => setEntries(prev => prev.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));

  const send = async () => {
    const collaborators = entries
      .map(e => ({ email: e.email.trim(), first_name: e.first_name.trim() || undefined, last_name: e.last_name.trim() || undefined }))
      .filter(e => e.email);
    if (!collaborators.length) { setError('Add at least one email address.'); return; }
    if (collaborators.some(c => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email))) { setError('Enter a valid email address for every collaborator.'); return; }
    setSending(true);
    setError('');
    try {
      await portalApi.post('/v1/questionnaire-responses/portal/collaborators', { response_id: responseId, collaborators, message: message.trim() || undefined });
      onInvited();
    } catch (err) {
      setError(apiError(err, 'Could not send invites. Try again.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal title="Invite collaborators" width={620} onClose={() => !sending && onClose()} footer={
      <>
        <Button variant="secondary" onClick={onClose} disabled={sending}>Cancel</Button>
        <Button icon={IC.send} onClick={send} loading={sending}>{sending ? 'Sending…' : `Send invite${entries.filter(e => e.email.trim()).length > 1 ? 's' : ''}`}</Button>
      </>
    }>
      {existing.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.ink2, marginBottom: 8 }}>Already on this questionnaire</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {existing.map(c => (
              <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: T.slate, background: T.hover, borderRadius: 99, padding: '4px 10px 4px 4px' }}>
                <Avatar name={collabName(c)} size={20} /> {c.email}{!c.accepted && <span style={{ color: T.amber, fontWeight: 600 }}>· invited</span>}
              </span>
            ))}
          </div>
        </div>
      )}
      <div style={{ fontSize: 13, fontWeight: 600, color: T.ink2, marginBottom: 4 }}>Add people</div>
      <div style={{ fontSize: 12.5, color: T.muted, marginBottom: 12, lineHeight: 1.5 }}>
        Each person gets an email with their own link. They can answer questions and attach evidence; only you can submit.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {entries.map((e, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr 32px', gap: 8, alignItems: 'center' }}>
            <TextInput value={e.first_name} onChange={ev => update(i, { first_name: ev.target.value })} placeholder="First name" aria-label="First name" />
            <TextInput value={e.last_name} onChange={ev => update(i, { last_name: ev.target.value })} placeholder="Last name" aria-label="Last name" />
            <TextInput value={e.email} onChange={ev => update(i, { email: ev.target.value })} placeholder="Email address" type="email" aria-label="Email address" autoFocus={i === entries.length - 1} />
            {entries.length > 1 ? (
              <button onClick={() => setEntries(prev => prev.filter((_, idx) => idx !== i))} aria-label="Remove row" className="pw-nav"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.faint, padding: 6, borderRadius: 7, display: 'inline-flex' }}>
                <Icon d={IC.close} size={15} />
              </button>
            ) : <span />}
          </div>
        ))}
      </div>
      <button onClick={() => setEntries(prev => [...prev, blankEntry()])} className="pw-link"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: T.primary, fontWeight: 600, fontSize: 13, fontFamily: sans, cursor: 'pointer', padding: 0, margin: '12px 0 20px' }}>
        <Icon d={IC.plus} size={14} /> Add another person
      </button>
      <Field label="Message" hint="Included in the invitation email.">
        <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3} className="pw-input" style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }} />
      </Field>
      {error && <div style={{ marginTop: 14 }}><Alert tone="error">{error}</Alert></div>}
    </Modal>
  );
}
